import { useState } from 'react'
import {
  FileText,
  Bot,
  Globe,
  BookOpen,
  GraduationCap,
  Download,
  Eye,
  Loader2,
  FileQuestion,
} from 'lucide-react'
import { outputs as outputsApi, type JobOutput, type OutputType } from '../../lib/api'

const OUTPUT_META: Record<
  OutputType,
  { label: string; icon: React.ReactNode; color: string }
> = {
  sop: {
    label: 'SOP Document',
    icon: <FileText size={18} />,
    color: 'bg-blue-100 text-blue-700',
  },
  agent_prompt: {
    label: 'Agent Prompt',
    icon: <Bot size={18} />,
    color: 'bg-purple-100 text-purple-700',
  },
  website: {
    label: 'Web Page',
    icon: <Globe size={18} />,
    color: 'bg-green-100 text-green-700',
  },
  blog: {
    label: 'Blog Post',
    icon: <BookOpen size={18} />,
    color: 'bg-yellow-100 text-yellow-700',
  },
  course_module: {
    label: 'Course Module',
    icon: <GraduationCap size={18} />,
    color: 'bg-orange-100 text-orange-700',
  },
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

const PREVIEWABLE: OutputType[] = ['website', 'sop', 'blog', 'course_module']

interface OutputCardProps {
  output: JobOutput
}

export function OutputCard({ output }: OutputCardProps) {
  const meta = OUTPUT_META[output.type] ?? {
    label: output.type,
    icon: <FileQuestion size={18} />,
    color: 'bg-gray-100 text-gray-700',
  }

  const [isDownloading, setIsDownloading] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)

  const date = new Date(output.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const { url } = await outputsApi.getDownloadUrl(output.id)
      const a = document.createElement('a')
      a.href = url
      a.download = output.filename
      a.click()
    } catch (err) {
      console.error('Download failed', err)
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePreview = async () => {
    setIsPreviewing(true)
    try {
      const { url } = await outputsApi.getPreviewUrl(output.id)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Preview failed', err)
    } finally {
      setIsPreviewing(false)
    }
  }

  const canPreview = PREVIEWABLE.includes(output.type)

  return (
    <div className="card flex items-start gap-4">
      {/* Icon */}
      <div
        className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${meta.color}`}
      >
        {meta.icon}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="font-display text-sm font-semibold uppercase tracking-wide text-alk-black">
          {meta.label}
        </div>
        <div className="text-xs text-alk-gray mt-0.5 truncate">{output.filename}</div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-alk-gray">{formatBytes(output.sizeBytes)}</span>
          <span className="text-alk-gray text-xs">·</span>
          <span className="text-xs text-alk-gray">{date}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {canPreview && (
          <button
            onClick={handlePreview}
            disabled={isPreviewing}
            className="btn-ghost flex items-center gap-1.5 text-xs py-1.5 px-2.5"
            title="Preview"
          >
            {isPreviewing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Eye size={13} />
            )}
            <span className="hidden sm:inline">Preview</span>
          </button>
        )}
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-2.5"
          title="Download"
        >
          {isDownloading ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Download size={13} />
          )}
          <span className="hidden sm:inline">Download</span>
        </button>
      </div>
    </div>
  )
}
